import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  MessageCircle, X, Send, MinusSquare, Phone, Mail, Star,
  ChevronRight, Clock, CheckCheck, Wrench, ShieldCheck,
  Package, CreditCard, RotateCcw, Zap, ThumbsUp, MessageSquare,
  ChevronDown, Info, HeadphonesIcon, Thermometer
} from 'lucide-react';
import { useSettingsStore } from '../stores/settingsStore';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface ChatMessage {
  id: string;
  text: string;
  sender: 'user' | 'agent' | 'bot';
  timestamp: number;
  senderName?: string;
  type?: 'text' | 'quick-replies' | 'rating';
  quickReplies?: QuickReply[];
}

export interface ChatSession {
  id: string;
  startedAt: number;
  messages: ChatMessage[];
  visitorName: string;
  visitorEmail: string;
  visitorPhone?: string;
  isEnded: boolean;
  topic?: string;
  rating?: number;
  ratingNote?: string;
  status?: 'waiting' | 'active' | 'ended';
  lastAgentReply?: number;
  unreadByAdmin?: number;
}

interface QuickReply {
  id: string;
  label: string;
  payload: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'ar_chat_sessions_v2';
const CURRENT_SESSION_KEY = 'ar_chat_current_session_v2';

// Mesai saatleri
const WORK_HOURS = { start: 8, end: 20 }; // 08:00 - 20:00

function isWorkingHours(): boolean {
  const now = new Date();
  const day = now.getDay(); // 0=Pazar, 6=Cumartesi
  const hour = now.getHours();
  if (day === 0) return false; // Pazar kapalı
  return hour >= WORK_HOURS.start && hour < WORK_HOURS.end;
}

// ─── Topic Definitions ───────────────────────────────────────────────────────

interface Topic {
  id: string;
  label: string;
  icon: React.ReactNode;
  color: string;
  description: string;
}

const TOPICS: Topic[] = [
  {
    id: 'installation',
    label: 'Montaj & Kurulum',
    icon: <Wrench size={18} />,
    color: 'bg-orange-100 text-orange-700',
    description: 'Montaj süresi, ücreti ve süreci',
  },
  {
    id: 'warranty',
    label: 'Garanti & Servis',
    icon: <ShieldCheck size={18} />,
    color: 'bg-green-100 text-green-700',
    description: 'Garanti koşulları, arıza bildirimi',
  },
  {
    id: 'pricing',
    label: 'Fiyat & Teklif',
    icon: <CreditCard size={18} />,
    color: 'bg-blue-100 text-blue-700',
    description: 'Fiyat teklifi, toplu alım, kampanya',
  },
  {
    id: 'technical',
    label: 'Teknik Destek',
    icon: <Thermometer size={18} />,
    color: 'bg-purple-100 text-purple-700',
    description: 'Kapasite seçimi, enerji tasarrufu',
  },
  {
    id: 'order',
    label: 'Sipariş & Kargo',
    icon: <Package size={18} />,
    color: 'bg-yellow-100 text-yellow-700',
    description: 'Sipariş takibi, teslimat süresi',
  },
  {
    id: 'return',
    label: 'İade & Değişim',
    icon: <RotateCcw size={18} />,
    color: 'bg-red-100 text-red-700',
    description: 'İade şartları, değişim süreci',
  },
];

// ─── Bot Knowledge Base ───────────────────────────────────────────────────────

interface BotAnswer {
  keywords: string[];
  answer: string;
  quickReplies?: { label: string; payload: string }[];
}

const BOT_KB_STATIC: BotAnswer[] = [
  // MONTAJ
  {
    keywords: ['montaj', 'kaç gün', 'kurulum', 'ne zaman takılır', 'taktır', 'taktırma'],
    answer: '🔧 **Montaj Süreleri:**\n\n• Sipariş onayından itibaren **2-5 iş günü** içinde montaj ekibimiz sizi arar.\n• Büyük şehirlerde (İzmir, İstanbul, Ankara) genellikle **2-3 iş günü**\n• Diğer illerde **3-5 iş günü**\n• Montaj randevusu için tercih ettiğiniz gün/saati belirtebilirsiniz.\n\n⏰ Montaj: Haftaiçi 09:00–18:00, Cumartesi 09:00–15:00',
    quickReplies: [
      { label: 'Montaj ücreti nedir?', payload: 'montaj_ucreti' },
      { label: 'Montajcı kimler?', payload: 'montajci_kim' },
      { label: 'Randevu almak istiyorum', payload: 'randevu' },
    ],
  },
  {
    keywords: ['montaj ücreti', 'montaj_ucreti', 'montaj fiyat', 'kaça montaj', 'ücretli mi'],
    answer: '💰 **Montaj Ücretleri:**\n\n• **Standart split klima**: 800 – 1.200 ₺\n• **Kaset/Kanal tipi**: 1.500 – 2.500 ₺\n• **VRF/Merkezi sistem**: Keşif sonrası fiyatlandırılır\n\n✅ 5+ ürün alımında montaj %20 indirimli\n✅ Yeni yapı projeleri için özel fiyat verilir\n\nDetaylı teklif için adres ve ürün bilgilerinizi paylaşın.',
    quickReplies: [
      { label: 'Teklif istiyorum', payload: 'teklif' },
      { label: 'Toplu alım indirimi?', payload: 'toplu_alim' },
    ],
  },
  {
    keywords: ['montajci_kim', 'montajcı', 'teknisyen', 'yetkili', 'kim takar'],
    answer: '👷 **Montaj Ekibimiz:**\n\nTüm montajlar **yetkili AR Klima teknisyenleri** tarafından yapılır.\n\n• Teknik eğitimli, sertifikalı personel\n• Montaj sonrası **deneme çalışması** yapılır\n• Soğutucu gaz doldurma yetkisi mevcuttur\n• Montaj garantisi: **2 yıl**',
  },
  // GARANTİ
  {
    keywords: ['garanti', 'kaç yıl garanti', 'garanti süresi', 'warranty'],
    answer: '🛡️ **Garanti Kapsamı:**\n\n• **Kompresör**: 5 yıl\n• **Diğer parçalar**: 2 yıl\n• **Montaj işçiliği**: 2 yıl\n\n📋 Garanti kapsamı dışı durumlar:\n• Kullanıcı kaynaklı hasarlar\n• Doğal afetler\n• Yetkisiz müdahale',
    quickReplies: [
      { label: 'Arıza bildirmek istiyorum', payload: 'ariza' },
      { label: 'Servis talebi oluştur', payload: 'servis' },
    ],
  },
  {
    keywords: ['arıza', 'ariza', 'bozuldu', 'çalışmıyor', 'soğutmuyor', 'ses çıkarıyor', 'servis'],
    answer: '🔴 **Arıza / Servis Talebi:**\n\n**Servis hattımız:** __EMERGENCY_PHONE__ (7/24)\n\nServise başvururken hazır bulundurun:\n1. Ürün seri numarası (kullanım kılavuzunda)\n2. Satın alma tarihi ve faturası\n3. Arızanın açıklaması\n\n⚡ Garanti kapsamındaki arızalarda **ücretsiz servis** sağlanır.\n🕐 Ortalama servis süresi: **1-3 iş günü**',
    quickReplies: [
      { label: 'Garanti süresi?', payload: 'garanti' },
      { label: 'Parça değişimi?', payload: 'parca' },
    ],
  },
  // FİYAT & TEKLİF
  {
    keywords: ['fiyat', 'teklif', 'ne kadar', 'kaça', 'fiyatlandırma', 'toplu'],
    answer: '💵 **Fiyat Teklifi:**\n\nSize özel fiyat teklifi hazırlayabilmemiz için:\n\n• **Alan büyüklüğü** (m²)\n• **Bina tipi** (konut / ticari / sanayi)\n• **İhtiyaç duyulan ünite sayısı**\n• **Mevcut elektrik altyapısı** bilgilerini paylaşın.\n\n📞 Hızlı teklif: **__PHONE__**\n📧 Detaylı teklif: **__EMAIL__**',
    quickReplies: [
      { label: 'Toplu alım indirimi?', payload: 'toplu_alim' },
      { label: 'Kampanyalar?', payload: 'kampanya' },
      { label: 'Telefon numaramı bırakayım', payload: 'ara_beni' },
    ],
  },
  {
    keywords: ['toplu_alim', 'toplu alım', 'çok ürün', 'proje', 'apartman', 'site'],
    answer: '🏢 **Toplu Alım Fırsatları:**\n\n| Adet | İndirim |\n|------|--------|\n| 3-5 ünite | %5 |\n| 6-10 ünite | %10 |\n| 11-20 ünite | %15 |\n| 21+ ünite | Özel fiyat |\n\n✅ Proje satışlarında **ücretsiz keşif**\n✅ Fatura ertelemesi imkânı\n✅ Anahtar teslim kurulum seçeneği',
  },
  // TEKNİK
  {
    keywords: ['kapasite', 'btu', 'kaç btu', 'hangi klima', 'seçim', 'kaç tonluk', 'm2', 'metrekare'],
    answer: '❄️ **Kapasite Hesaplama Rehberi:**\n\n| Alan (m²) | Tavsiye Edilen BTU |\n|-----------|-------------------|\n| 10-15 m² | 9.000 BTU |\n| 16-25 m² | 12.000 BTU |\n| 26-35 m² | 18.000 BTU |\n| 36-45 m² | 24.000 BTU |\n| 46-60 m² | 30.000 BTU |\n\n⚠️ Yüksek tavan, güneş alan cephe ve sanayi ortamı için +%20 ekleyin.\n\n🆓 Ücretsiz yerinde kapasite hesabı için ekibimizi arayın!',
    quickReplies: [
      { label: 'Enerji tasarrufu nedir?', payload: 'enerji' },
      { label: 'Inverter klima nedir?', payload: 'inverter' },
    ],
  },
  {
    keywords: ['enerji', 'enerji_tasarrufu', 'elektrik', 'fatura', 'a sınıfı', 'inverter'],
    answer: '⚡ **Enerji Tasarrufu:**\n\n• **A+++ sınıfı** klimalar, eski modellere göre **%60\'a kadar** enerji tasarrufu sağlar.\n• **Inverter teknolojisi** ile kompresör sürekli çalışmaz, gerektiğinde hız ayarlar.\n• Yıllık ortalama tasarruf: **2.000 – 5.000 ₺** (kullanıma göre değişir)\n\n🌿 Tüm ürünlerimiz enerji etiketlidir. Ürün sayfasında görebilirsiniz.',
  },
  // SİPARİŞ & KARGO
  {
    keywords: ['sipariş', 'kargo', 'teslimat', 'ne zaman gelir', 'takip', 'gönderim'],
    answer: '📦 **Sipariş & Teslimat:**\n\n• Ödeme onayından sonra **1-2 iş günü** içinde kargoya verilir.\n• Teslimat süresi: **3-5 iş günü** (ülke geneli)\n• İzmir içi: **1-2 gün**\n\n📬 Kargo takip numarası SMS ile gönderilir.\n\n⚠️ Klima montajı için kargo teslimatı **ayrı** planlanır. Montaj ekibi sizi arayacaktır.',
    quickReplies: [
      { label: 'Montaj ne zaman?', payload: 'montaj' },
      { label: 'Faturamı görmek istiyorum', payload: 'fatura' },
    ],
  },
  // İADE & DEĞİŞİM
  {
    keywords: ['iade', 'değişim', 'geri iade', 'iptal', 'cayma', 'iade şartları'],
    answer: '🔄 **İade & Değişim Koşulları:**\n\n**Teslimattan itibaren 14 gün** içinde iade hakkınız mevcuttur.\n\n✅ İade koşulları:\n• Ürün kullanılmamış olmalı\n• Orijinal ambalajında olmalı\n• Tüm aksesuarlar tam olmalı\n• Fatura ile birlikte\n\n❌ Montajı yapılmış ürünler iade edilemez (garanti kapsamı hariç).\n\n📞 İade talebi: **__PHONE__**',
    quickReplies: [
      { label: 'Arıza var, servis istiyorum', payload: 'servis' },
      { label: 'Garanti koşulları?', payload: 'garanti' },
    ],
  },
  // GENEL
  {
    keywords: ['merhaba', 'selam', 'iyi günler', 'iyi sabahlar', 'hello'],
    answer: '👋 Merhaba! AR Klima\'ya hoş geldiniz. Size nasıl yardımcı olabilirim?\n\nAşağıdaki konulardan birini seçerek hızlıca yanıt alabilirsiniz 👇',
  },
];

function buildBotKB(phone: string, emergencyPhone: string, email: string): BotAnswer[] {
  return BOT_KB_STATIC.map(entry => ({
    ...entry,
    answer: entry.answer
      .replace(/__PHONE__/g, phone || '0232 504 07 01')
      .replace(/__EMERGENCY_PHONE__/g, emergencyPhone || '0850 000 00 00')
      .replace(/__EMAIL__/g, email || 'info@arklima.com'),
  }));
}

function findBotAnswer(text: string, kb: BotAnswer[]): BotAnswer | null {
  const lower = text.toLowerCase();
  for (const entry of kb) {
    if (entry.keywords.some(k => lower.includes(k))) {
      return entry;
    }
  }
  return null;
}

// ─── Storage Helpers ──────────────────────────────────────────────────────────

function generateId() {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
}

function saveSession(session: ChatSession) {
  try {
    const sessions: ChatSession[] = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    const idx = sessions.findIndex(s => s.id === session.id);
    if (idx >= 0) sessions[idx] = session;
    else sessions.push(session);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions));
    localStorage.setItem(CURRENT_SESSION_KEY, session.id);
  } catch (_) {}
}

export function getAllChatSessions(): ChatSession[] {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch (_) {
    return [];
  }
}

export function sendAdminReply(sessionId: string, text: string): void {
  try {
    const sessions: ChatSession[] = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    const idx = sessions.findIndex(s => s.id === sessionId);
    if (idx < 0) return;
    const newMsg: ChatMessage = {
      id: generateId(),
      text,
      sender: 'agent',
      senderName: 'AR Müşteri Temsilcisi',
      timestamp: Date.now(),
    };
    sessions[idx].messages.push(newMsg);
    sessions[idx].status = 'active';
    sessions[idx].lastAgentReply = Date.now();
    sessions[idx].unreadByAdmin = 0;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions));
  } catch (_) {}
}

export function endChatSession(sessionId: string): void {
  try {
    const sessions: ChatSession[] = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    const idx = sessions.findIndex(s => s.id === sessionId);
    if (idx < 0) return;
    sessions[idx].isEnded = true;
    sessions[idx].status = 'ended';
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions));
  } catch (_) {}
}

// ─── Main Component ───────────────────────────────────────────────────────────

const ARLiveChat: React.FC = () => {
  const {
    phoneNumber: storePhone,
    phoneEmergency: storeEmergencyPhone,
    whatsappNumber: storeWAPhone,
    email: storeEmail,
  } = useSettingsStore();

  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputText, setInputText] = useState('');
  const [session, setSession] = useState<ChatSession | null>(null);
  const [step, setStep] = useState<'form' | 'topic' | 'chat' | 'rating'>('form');
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });
  const [selectedTopic, setSelectedTopic] = useState<Topic | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [rating, setRating] = useState(0);
  const [ratingNote, setRatingNote] = useState('');
  const [ratingSubmitted, setRatingSubmitted] = useState(false);
  const [hoveredStar, setHoveredStar] = useState(0);
  const [showQuickReplies, setShowQuickReplies] = useState(true);

  // Dynamic KB built with store values so phone numbers are always up-to-date
  const BOT_KB = React.useMemo(
    () => buildBotKB(storePhone, storeEmergencyPhone, storeEmail),
    [storePhone, storeEmergencyPhone, storeEmail]
  );
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const online = isWorkingHours();

  // Load existing session on mount
  useEffect(() => {
    const sessionId = localStorage.getItem(CURRENT_SESSION_KEY);
    if (sessionId) {
      const sessions: ChatSession[] = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      const existing = sessions.find(s => s.id === sessionId);
      if (existing && !existing.isEnded) {
        setSession(existing);
        setMessages(existing.messages);
        const topic = TOPICS.find(t => t.id === existing.topic) || null;
        setSelectedTopic(topic);
        setFormData({
          name: existing.visitorName,
          email: existing.visitorEmail,
          phone: existing.visitorPhone || '',
        });
        setStep('chat');
      }
    }
  }, []);

  // Poll for admin replies every 3s
  useEffect(() => {
    if (!session) return;
    const interval = setInterval(() => {
      try {
        const sessions: ChatSession[] = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
        const updated = sessions.find(s => s.id === session.id);
        if (updated && updated.messages.length !== messages.length) {
          setMessages(updated.messages);
          setSession(updated);
          if (!isOpen || isMinimized) {
            const newAgentMsgs = updated.messages.slice(messages.length).filter(m => m.sender === 'agent');
            if (newAgentMsgs.length > 0) setUnreadCount(prev => prev + newAgentMsgs.length);
          }
        }
      } catch (_) {}
    }, 3000);
    return () => clearInterval(interval);
  }, [session, messages.length, isOpen, isMinimized]);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  }, []);

  useEffect(() => {
    if (isOpen && !isMinimized) {
      scrollToBottom();
      setUnreadCount(0);
    }
  }, [isOpen, isMinimized, messages, scrollToBottom]);

  const addMessage = useCallback((msg: Omit<ChatMessage, 'id' | 'timestamp'>, currentSession?: ChatSession) => {
    const sess = currentSession || session;
    const newMsg: ChatMessage = { ...msg, id: generateId(), timestamp: Date.now() };
    setMessages(prev => {
      const updated = [...prev, newMsg];
      if (sess) {
        const updatedSess: ChatSession = {
          ...sess,
          messages: updated,
          unreadByAdmin: (sess.unreadByAdmin || 0) + (msg.sender === 'user' ? 1 : 0),
        };
        setSession(updatedSess);
        saveSession(updatedSess);
      }
      return updated;
    });
    return newMsg;
  }, [session]);

  const startSession = useCallback(() => {
    const { name, email, phone } = formData;
    if (!name.trim()) return;

    const newSession: ChatSession = {
      id: generateId(),
      startedAt: Date.now(),
      messages: [],
      visitorName: name.trim(),
      visitorEmail: email.trim(),
      visitorPhone: phone.trim(),
      isEnded: false,
      topic: selectedTopic?.id,
      status: 'waiting',
      unreadByAdmin: 0,
    };
    setSession(newSession);
    saveSession(newSession);
    setStep('chat');

    // Welcome message
    setTimeout(() => {
      const topicLabel = selectedTopic ? ` (${selectedTopic.label})` : '';
      const workMsg = online
        ? 'Bir müşteri temsilcisi en kısa sürede sizinle ilgilenecek. ⏱️ Ortalama yanıt süresi: 2-5 dakika'
        : '⚠️ Şu an mesai saatleri dışındasınız. Mesajınızı bırakın, **Pazartesi–Cumartesi 08:00–20:00** arasında size geri döneceğiz.';

      const welcomeMsg: ChatMessage = {
        id: generateId(),
        text: `Merhaba **${name.trim()}**! AR Klima Müşteri Hizmetleri'ne hoş geldiniz${topicLabel}. 🌡️\n\n${workMsg}`,
        sender: 'bot',
        senderName: 'AR Bot',
        timestamp: Date.now(),
      };

      setMessages([welcomeMsg]);
      const sess1: ChatSession = { ...newSession, messages: [welcomeMsg] };
      setSession(sess1);
      saveSession(sess1);

      // Topic-specific quick start
      if (selectedTopic) {
        setTimeout(() => {
          const topicQR = getTopicQuickReplies(selectedTopic.id);
          if (topicQR.length > 0) {
            const qrMsg: ChatMessage = {
              id: generateId(),
              text: `${selectedTopic.label} konusunda sık sorulan sorular:`,
              sender: 'bot',
              senderName: 'AR Bot',
              timestamp: Date.now() + 1,
              type: 'quick-replies',
              quickReplies: topicQR,
            };
            setMessages(prev => {
              const updated = [...prev, qrMsg];
              const sess2: ChatSession = { ...sess1, messages: updated };
              setSession(sess2);
              saveSession(sess2);
              return updated;
            });
          }
        }, 800);
      }
    }, 400);
  }, [formData, selectedTopic, online, session]);

  function getTopicQuickReplies(topicId: string): QuickReply[] {
    const map: Record<string, { label: string; payload: string }[]> = {
      installation: [
        { label: 'Montaj kaç gün sürer?', payload: 'montaj' },
        { label: 'Montaj ücreti nedir?', payload: 'montaj ücreti' },
        { label: 'Montajcı kimler?', payload: 'montajci_kim' },
        { label: 'Randevu almak istiyorum', payload: 'randevu almak istiyorum' },
      ],
      warranty: [
        { label: 'Garanti süresi nedir?', payload: 'garanti süresi' },
        { label: 'Arıza bildirmek istiyorum', payload: 'arıza bozuldu' },
        { label: 'Servis talebi oluşturmak istiyorum', payload: 'servis' },
      ],
      pricing: [
        { label: 'Fiyat teklifi almak istiyorum', payload: 'fiyat teklifi' },
        { label: 'Toplu alım indirimi var mı?', payload: 'toplu alım' },
        { label: 'Güncel kampanyalar nedir?', payload: 'kampanya' },
      ],
      technical: [
        { label: 'Hangi BTU seçmeliyim?', payload: 'kapasite btu m2' },
        { label: 'Enerji tasarrufu hakkında bilgi', payload: 'enerji tasarrufu' },
        { label: 'Inverter klima nedir?', payload: 'inverter' },
      ],
      order: [
        { label: 'Siparişim ne zaman gelir?', payload: 'sipariş teslimat' },
        { label: 'Kargo takibi yapmak istiyorum', payload: 'kargo takip' },
        { label: 'Fatura bilgilerim', payload: 'fatura' },
      ],
      return: [
        { label: 'İade şartları nelerdir?', payload: 'iade' },
        { label: 'Ürünümü değiştirmek istiyorum', payload: 'değişim' },
        { label: 'İptal etmek istiyorum', payload: 'iptal' },
      ],
    };
    return (map[topicId] || []).map(qr => ({ ...qr, id: generateId() }));
  }

  const sendMessage = useCallback((text?: string) => {
    const txt = (text || inputText).trim();
    if (!txt) return;
    setInputText('');
    setShowQuickReplies(false);

    const userMsg: ChatMessage = {
      id: generateId(),
      text: txt,
      sender: 'user',
      senderName: formData.name || 'Siz',
      timestamp: Date.now(),
    };

    setMessages(prev => {
      const updated = [...prev, userMsg];
      if (session) {
        const upd: ChatSession = {
          ...session,
          messages: updated,
          unreadByAdmin: (session.unreadByAdmin || 0) + 1,
        };
        setSession(upd);
        saveSession(upd);
      }
      return updated;
    });

    // Bot response
    const botAnswer = findBotAnswer(txt, BOT_KB);
    if (botAnswer) {
      setIsTyping(true);
      const delay = 1000 + txt.length * 20;
      setTimeout(() => {
        setIsTyping(false);
        const botMsg: ChatMessage = {
          id: generateId(),
          text: botAnswer.answer,
          sender: 'bot',
          senderName: 'AR Bot',
          timestamp: Date.now(),
          type: botAnswer.quickReplies ? 'quick-replies' : 'text',
          quickReplies: botAnswer.quickReplies?.map(qr => ({ ...qr, id: generateId() })),
        };
        setMessages(prev => {
          const updated = [...prev, botMsg];
          if (session) {
            const upd: ChatSession = { ...session, messages: updated };
            setSession(upd);
            saveSession(upd);
          }
          return updated;
        });
        scrollToBottom();
      }, delay);
    } else {
      // Generic fallback
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        const fallbackMsg: ChatMessage = {
          id: generateId(),
          text: online
            ? `📝 Mesajınız alındı. Müşteri temsilcimiz en kısa sürede yanıt verecek.\n\nHızlı bilgi için: **${storePhone || '0232 XXX XX XX'}** veya **${storeEmergencyPhone || '0850 XXX XX XX'}**`
            : `📝 Mesajınız alındı. Mesai saatlerimiz dışında olduğunuz için ilk fırsatta size ulaşacağız.\n\n📞 Acil durumlar için: **${storeEmergencyPhone || '0850 XXX XX XX'}** (7/24)`,
          sender: 'bot',
          senderName: 'AR Bot',
          timestamp: Date.now(),
        };
        setMessages(prev => {
          const updated = [...prev, fallbackMsg];
          if (session) {
            const upd: ChatSession = { ...session, messages: updated };
            setSession(upd);
            saveSession(upd);
          }
          return updated;
        });
        scrollToBottom();
      }, 1500);
    }
  }, [inputText, session, formData.name, online, scrollToBottom]);

  const handleQuickReply = (payload: string, label: string) => {
    sendMessage(label);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const submitRating = () => {
    if (rating === 0) return;
    if (session) {
      const sessions: ChatSession[] = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      const idx = sessions.findIndex(s => s.id === session.id);
      if (idx >= 0) {
        sessions[idx].rating = rating;
        sessions[idx].ratingNote = ratingNote;
        sessions[idx].isEnded = true;
        sessions[idx].status = 'ended';
        localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions));
      }
    }
    setRatingSubmitted(true);
  };

  const formatTime = (ts: number) => {
    const d = new Date(ts);
    return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  };

  const renderMessage = (msg: ChatMessage) => {
    const isUser = msg.sender === 'user';
    // Convert **bold** markdown to JSX
    const renderText = (text: string) => {
      const parts = text.split(/(\*\*[^*]+\*\*)/g);
      return parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={i}>{part.slice(2, -2)}</strong>;
        }
        return <span key={i}>{part}</span>;
      });
    };

    return (
      <div key={msg.id} className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
        {!isUser && (
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-cyan-600 to-cyan-800 flex items-center justify-center text-white text-xs font-bold mr-2 flex-shrink-0 mt-1 shadow">
            {msg.sender === 'agent' ? '👤' : 'AR'}
          </div>
        )}
        <div className="flex flex-col max-w-[80%]">
          {!isUser && msg.senderName && (
            <span className="text-xs text-gray-400 mb-1 ml-1">{msg.senderName}</span>
          )}
          <div className={`rounded-2xl px-3 py-2.5 text-sm leading-relaxed ${
            isUser
              ? 'bg-gradient-to-br from-cyan-600 to-cyan-800 text-white rounded-br-sm shadow-md'
              : 'bg-white text-gray-800 shadow-sm rounded-bl-sm border border-gray-100'
          }`}>
            <p className="whitespace-pre-line">{renderText(msg.text)}</p>
            <p className={`text-xs mt-1.5 flex items-center gap-1 ${isUser ? 'text-cyan-200 justify-end' : 'text-gray-400'}`}>
              <Clock size={10} />
              {formatTime(msg.timestamp)}
              {isUser && <CheckCheck size={11} className="text-cyan-200" />}
            </p>
          </div>
          {/* Quick replies */}
          {msg.type === 'quick-replies' && msg.quickReplies && showQuickReplies && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {msg.quickReplies.map(qr => (
                <button
                  key={qr.id}
                  onClick={() => handleQuickReply(qr.payload, qr.label)}
                  className="px-3 py-1.5 text-xs bg-cyan-50 hover:bg-cyan-100 text-cyan-700 border border-cyan-200 rounded-full transition-colors font-medium"
                >
                  {qr.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── Render sections ──────────────────────────────────────────────────────────

  const renderForm = () => (
    <div className="p-5 flex flex-col gap-4 overflow-y-auto">
      <div className="text-center">
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cyan-100 to-cyan-200 flex items-center justify-center mx-auto mb-3 shadow">
          <MessageCircle className="text-cyan-700" size={30} />
        </div>
        <h3 className="font-bold text-gray-800 text-lg">Merhaba! 👋</h3>
        <p className="text-gray-500 text-sm mt-1">
          {online
            ? '🟢 Çevrimiçi · Ortalama yanıt: 2-5 dk'
            : '🔴 Mesai dışı · Mesajınızı bırakın'}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs text-gray-500 bg-gray-50 rounded-xl p-3">
        <div className="flex items-center gap-1.5">
          <Clock size={12} className="text-cyan-600" />
          <span>Hft içi: 08:00–20:00</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Clock size={12} className="text-cyan-600" />
          <span>Cumartesi: 09:00–17:00</span>
        </div>
        <div className="flex items-center gap-1.5 col-span-2">
          <Phone size={12} className="text-cyan-600" />
          <span>{storePhone || '0232 XXX XX XX'} · {storeEmergencyPhone || '0850 XXX XX XX'} (7/24 Arıza)</span>
        </div>
      </div>

      <div className="space-y-2.5">
        <input
          type="text"
          placeholder="Adınız Soyadınız *"
          value={formData.name}
          onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
          className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition"
        />
        <input
          type="tel"
          placeholder="Telefon Numaranız (opsiyonel)"
          value={formData.phone}
          onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))}
          className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition"
        />
        <input
          type="email"
          placeholder="E-posta (opsiyonel)"
          value={formData.email}
          onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
          className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition"
        />
      </div>

      <button
        onClick={() => formData.name.trim() && setStep('topic')}
        disabled={!formData.name.trim()}
        className="w-full bg-gradient-to-r from-cyan-600 to-cyan-800 hover:from-cyan-500 hover:to-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-xl py-3 text-sm transition-all shadow-md"
      >
        Devam Et →
      </button>

      {/* WhatsApp quick link */}
      <a
        href={`https://wa.me/${storeWAPhone || '905321234567'}?text=${encodeURIComponent('Merhaba! AR Klima hakkında bilgi almak istiyorum.')}`}
        target="_blank"
        rel="noopener noreferrer"
        className="w-full flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20bd5a] text-white font-semibold rounded-xl py-2.5 text-sm transition-colors shadow-md"
      >
        <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
        WhatsApp ile Yazın
      </a>

      <div className="flex items-center gap-1 text-xs text-gray-400 justify-center">
        <div className="h-px flex-1 bg-gray-100" />
        <span>veya</span>
        <div className="h-px flex-1 bg-gray-100" />
      </div>

      <div className="flex items-center gap-4 text-xs text-gray-400 justify-center">
        <a href={`tel:${(storePhone || '02321234567').replace(/\s/g, '')}`} className="flex items-center gap-1 hover:text-cyan-700 transition-colors">
          <Phone size={12} />
          <span>Telefon</span>
        </a>
        <a href="mailto:info@arklima.com" className="flex items-center gap-1 hover:text-cyan-700 transition-colors">
          <Mail size={12} />
          <span>E-posta</span>
        </a>
      </div>
    </div>
  );

  const renderTopicSelect = () => (
    <div className="p-4 flex flex-col gap-3 overflow-y-auto">
      <div className="text-center">
        <p className="font-semibold text-gray-800">Konunuzu seçin</p>
        <p className="text-gray-500 text-xs mt-1">Daha hızlı ve doğru yardım alın</p>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {TOPICS.map(topic => (
          <button
            key={topic.id}
            onClick={() => {
              setSelectedTopic(topic);
              startSession();
            }}
            className={`flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all text-center hover:shadow-md ${
              selectedTopic?.id === topic.id
                ? 'border-cyan-500 bg-cyan-50'
                : 'border-gray-200 hover:border-cyan-300 bg-white'
            }`}
          >
            <span className={`p-2 rounded-full ${topic.color}`}>{topic.icon}</span>
            <span className="text-xs font-medium text-gray-700 leading-tight">{topic.label}</span>
          </button>
        ))}
      </div>
      <button
        onClick={() => { setSelectedTopic(null); startSession(); }}
        className="text-xs text-gray-400 hover:text-cyan-600 transition-colors underline text-center"
      >
        Konu seçmeden devam et
      </button>
    </div>
  );

  const renderChat = () => (
    <>
      {/* Topic badge */}
      {selectedTopic && (
        <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
          <span className={`p-1 rounded-full ${selectedTopic.color} text-xs`}>{selectedTopic.icon}</span>
          <span className="text-xs text-gray-500 font-medium">{selectedTopic.label}</span>
          <span className="ml-auto text-xs text-gray-400 flex items-center gap-1">
            <span className={`w-2 h-2 rounded-full ${online ? 'bg-green-400' : 'bg-gray-400'}`} />
            {online ? 'Çevrimiçi' : 'Mesai dışı'}
          </span>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 bg-gray-50 min-h-0">
        {messages.map(renderMessage)}

        {/* Typing indicator */}
        {isTyping && (
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-cyan-600 to-cyan-800 flex items-center justify-center text-white text-xs font-bold shadow">
              AR
            </div>
            <div className="bg-white border border-gray-100 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
              <div className="flex gap-1 items-center">
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Quick reply bar */}
      <div className="px-3 pt-2 flex gap-1.5 overflow-x-auto pb-1 border-t border-gray-100 bg-white scrollbar-hide">
        {[
          { label: '🔧 Montaj', payload: 'montaj' },
          { label: '❄️ BTU Hesap', payload: 'kapasite' },
          { label: '🛡️ Garanti', payload: 'garanti süresi' },
          { label: '💰 Teklif', payload: 'fiyat teklifi' },
          { label: '📦 Kargo', payload: 'sipariş kargo' },
          { label: '🔄 İade', payload: 'iade' },
        ].map(qr => (
          <button
            key={qr.label}
            onClick={() => sendMessage(qr.label)}
            className="flex-shrink-0 px-2.5 py-1 text-xs bg-cyan-50 hover:bg-cyan-100 text-cyan-700 border border-cyan-200 rounded-full transition-colors"
          >
            {qr.label}
          </button>
        ))}
      </div>

      {/* Input */}
      <div className="p-3 bg-white flex items-center gap-2">
        <input
          ref={inputRef}
          type="text"
          placeholder="Sorunuzu yazın..."
          value={inputText}
          onChange={e => setInputText(e.target.value)}
          onKeyDown={handleKeyDown}
          className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition"
        />
        <button
          onClick={() => sendMessage()}
          disabled={!inputText.trim()}
          className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-600 to-cyan-800 hover:from-cyan-500 hover:to-cyan-700 disabled:opacity-40 text-white flex items-center justify-center transition-all flex-shrink-0 shadow"
        >
          <Send size={14} />
        </button>
      </div>

      {/* End chat + WhatsApp switch */}
      <div className="px-3 pb-2 flex items-center justify-between">
        <a
          href={`https://wa.me/905321234567?text=${encodeURIComponent('Merhaba! AR Klima canlı destek üzerinden yazıyorum, yardım almak istiyorum.')}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-green-600 hover:text-green-700 flex items-center gap-1 transition-colors font-medium"
        >
          <svg viewBox="0 0 24 24" className="w-3 h-3" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          WhatsApp'a geç
        </a>
        <button
          onClick={() => setStep('rating')}
          className="text-xs text-gray-400 hover:text-red-500 transition-colors"
        >
          Sohbeti bitir
        </button>
      </div>
    </>
  );

  const renderRating = () => (
    <div className="p-6 flex flex-col items-center gap-4 text-center">
      {!ratingSubmitted ? (
        <>
          <div className="w-14 h-14 rounded-full bg-cyan-100 flex items-center justify-center mx-auto">
            <ThumbsUp className="text-cyan-700" size={26} />
          </div>
          <h3 className="font-bold text-gray-800">Nasıl bir deneyimdi?</h3>
          <p className="text-gray-500 text-sm">Hizmetimizi değerlendirin</p>
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map(star => (
              <button
                key={star}
                onMouseEnter={() => setHoveredStar(star)}
                onMouseLeave={() => setHoveredStar(0)}
                onClick={() => setRating(star)}
                className="transition-transform hover:scale-110"
              >
                <Star
                  size={32}
                  className={`transition-colors ${
                    star <= (hoveredStar || rating)
                      ? 'fill-yellow-400 text-yellow-400'
                      : 'text-gray-300'
                  }`}
                />
              </button>
            ))}
          </div>
          {rating > 0 && (
            <p className="text-sm text-gray-500">
              {['', 'Çok kötü 😞', 'Kötü 😕', 'Orta 😐', 'İyi 😊', 'Mükemmel 🌟'][rating]}
            </p>
          )}
          <textarea
            placeholder="Yorumunuzu yazın (opsiyonel)..."
            value={ratingNote}
            onChange={e => setRatingNote(e.target.value)}
            rows={3}
            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-cyan-500 resize-none"
          />
          <div className="flex gap-2 w-full">
            <button
              onClick={() => setStep('chat')}
              className="flex-1 border border-gray-200 text-gray-500 rounded-xl py-2.5 text-sm hover:bg-gray-50 transition"
            >
              Geri Dön
            </button>
            <button
              onClick={submitRating}
              disabled={rating === 0}
              className="flex-1 bg-cyan-700 hover:bg-cyan-800 disabled:opacity-40 text-white rounded-xl py-2.5 text-sm font-semibold transition"
            >
              Gönder
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
            <ThumbsUp className="text-green-600" size={30} />
          </div>
          <h3 className="font-bold text-gray-800">Teşekkürler! 🎉</h3>
          <p className="text-gray-500 text-sm">Geri bildiriminiz bizim için çok değerli.</p>
          <button
            onClick={() => {
              setIsOpen(false);
              setStep('form');
              setMessages([]);
              setSession(null);
              setRating(0);
              setRatingNote('');
              setRatingSubmitted(false);
              setShowQuickReplies(true);
              localStorage.removeItem(CURRENT_SESSION_KEY);
            }}
            className="w-full bg-cyan-700 hover:bg-cyan-800 text-white rounded-xl py-2.5 text-sm font-semibold transition"
          >
            Pencereyi Kapat
          </button>
        </>
      )}
    </div>
  );

  // ── Main return ────────────────────────────────────────────────────────────

  return (
    <div className="fixed bottom-6 right-4 z-50 flex flex-col items-end gap-2">
      {/* Chat Window */}
      {isOpen && !isMinimized && (
        <div
          className="w-72 sm:w-80 bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
          style={{ maxHeight: '480px', minHeight: '320px' }}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-cyan-700 to-cyan-900 px-3 py-2 flex items-center gap-2.5 flex-shrink-0">
            <div className="relative flex-shrink-0">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white font-bold text-xs shadow-inner">
                AR
              </div>
              <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-cyan-800 ${online ? 'bg-green-400' : 'bg-gray-400'}`} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-semibold text-xs">AR Müşteri Hizmetleri</p>
              <p className="text-cyan-200 text-[10px] truncate">
                {online ? '🟢 Çevrimiçi · Yanıt: 2-5 dk' : '🔴 Mesai dışı · Mesaj bırakın'}
              </p>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={() => setIsMinimized(true)}
                className="w-7 h-7 rounded-full hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                title="Küçült"
              >
                <MinusSquare size={14} />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="w-7 h-7 rounded-full hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                title="Kapat"
              >
                <X size={14} />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
            {step === 'form' && renderForm()}
            {step === 'topic' && renderTopicSelect()}
            {step === 'chat' && renderChat()}
            {step === 'rating' && renderRating()}
          </div>
        </div>
      )}

      {/* Minimized banner */}
      {isOpen && isMinimized && (
        <div
          onClick={() => setIsMinimized(false)}
          className="bg-gradient-to-r from-cyan-700 to-cyan-900 text-white px-4 py-2.5 rounded-2xl shadow-lg flex items-center gap-2 cursor-pointer hover:shadow-xl transition-shadow"
        >
          <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold">AR</div>
          <span className="text-sm font-medium">AR Müşteri Hizmetleri</span>
          {unreadCount > 0 && (
            <span className="w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
              {unreadCount}
            </span>
          )}
          <button
            onClick={e => { e.stopPropagation(); setIsOpen(false); setIsMinimized(false); }}
            className="ml-1 hover:bg-white/20 rounded-full p-0.5"
          >
            <X size={13} />
          </button>
        </div>
      )}

      {/* Chat Button */}
      {!isOpen && (
        <div className="flex flex-col items-end gap-2">
          {/* Pulse hint */}
          <div className="bg-white rounded-xl shadow-lg px-3 py-2 text-xs text-gray-600 border border-gray-100 max-w-[180px] text-right animate-pulse">
            💬 Nasıl yardımcı olabiliriz?
          </div>
          <button
            onClick={() => { setIsOpen(true); setIsMinimized(false); setUnreadCount(0); }}
            className="relative w-14 h-14 bg-gradient-to-br from-cyan-600 to-cyan-800 hover:from-cyan-500 hover:to-cyan-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center group"
            title="AR Müşteri Hizmetleri"
          >
            <MessageCircle size={26} className="transition-transform group-hover:scale-110" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                {unreadCount}
              </span>
            )}
            <span className="absolute -top-1 -left-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white" />
          </button>
        </div>
      )}
    </div>
  );
};

export default ARLiveChat;
